import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, Loader2, Star, Edit2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['cyan', 'blue', 'purple', 'green', 'indigo', 'amber', 'rose'];

const blank = {
  amount: 500,
  bonus: 40,
  label: '',
  color: 'cyan',
  popular: false,
  enabled: true,
  order: 99,
};

export default function BonusPackagesEditor({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {...item} or null
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/bonus-packages`);
      if (!r.ok) { setItems([]); return; }
      setItems(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!(editing.amount > 0)) { alert('Amount must be > 0'); return; }
    if (editing.bonus < 0) { alert('Bonus cannot be negative'); return; }
    setSaving(true);
    try {
      const isNew = !editing.id;
      const url = isNew ? `${API_URL}/api/config/bonus-packages` : `${API_URL}/api/config/bonus-packages/${editing.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, { method, headers, body: JSON.stringify(editing) });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete bonus package "€${pkg.amount} → +€${pkg.bonus}"?`)) return;
    const r = await fetch(`${API_URL}/api/config/bonus-packages/${pkg.id}`, { method: 'DELETE', headers });
    if (!r.ok) { alert('Delete failed: ' + await r.text()); return; }
    load();
  };

  const togglePopular = async (pkg) => {
    const r = await fetch(`${API_URL}/api/config/bonus-packages/${pkg.id}`, {
      method: 'PATCH', headers, body: JSON.stringify({ popular: !pkg.popular }),
    });
    if (r.ok) load();
  };

  const toggleEnabled = async (pkg) => {
    const r = await fetch(`${API_URL}/api/config/bonus-packages/${pkg.id}`, {
      method: 'PATCH', headers, body: JSON.stringify({ enabled: !pkg.enabled }),
    });
    if (r.ok) load();
  };

  return (
    <div className="space-y-5" data-testid="bonus-packages-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-slate-800">Bonus Packages</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            When the customer recharges by one of these amounts, an extra <em>bonus</em> credit
            is automatically added (separate transaction, no invoice).
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...blank, order: (items.length + 1) })}
          data-testid="add-bonus-package"
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-md flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" /> New package
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-600 bg-white rounded-xl border border-slate-200">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">
          No bonus packages yet — click "New package" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`bonus-package-${p.amount}`}
              className={`relative bg-white rounded-xl border-2 p-5 shadow-sm transition-all ${
                p.enabled ? 'border-slate-200' : 'border-slate-200 opacity-60'
              } ${p.popular ? 'ring-2 ring-amber-300' : ''}`}
            >
              {p.popular && (
                <div className="absolute -top-3 right-4 px-3 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-extrabold uppercase tracking-wider rounded-full shadow">
                  ★ Popular
                </div>
              )}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-extrabold text-slate-800">€{p.amount}</span>
                <span className="text-slate-400 text-xs">recharge</span>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold mb-3">
                + €{p.bonus} bonus
              </div>
              {p.label && <div className="text-sm text-slate-600 mb-3">{p.label}</div>}
              <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-500 mb-3">
                <div>Color: <span className="font-semibold">{p.color}</span></div>
                <div>Order: <span className="font-semibold">{p.order}</span></div>
                <div className={`font-semibold ${p.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {p.enabled ? '● Enabled' : '○ Hidden'}
                </div>
                <div>Total: <span className="font-semibold text-slate-800">€{(p.amount + p.bonus).toFixed(0)}</span></div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setEditing({ ...p })}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-cyan-50 text-cyan-700 text-xs font-bold rounded hover:bg-cyan-100"
                  data-testid={`edit-bonus-${p.amount}`}
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => togglePopular(p)}
                  className={`px-3 py-1.5 text-xs font-bold rounded ${p.popular ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'} hover:opacity-80`}
                  title={p.popular ? 'Unmark popular' : 'Mark as popular'}
                >
                  <Star className={`w-3 h-3 ${p.popular ? 'fill-amber-500' : ''}`} />
                </button>
                <button
                  onClick={() => toggleEnabled(p)}
                  className={`px-3 py-1.5 text-xs font-bold rounded ${p.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'} hover:opacity-80`}
                  title={p.enabled ? 'Hide from clients' : 'Show to clients'}
                >
                  {p.enabled ? '●' : '○'}
                </button>
                <button
                  onClick={() => remove(p)}
                  className="px-3 py-1.5 bg-rose-50 text-rose-700 text-xs font-bold rounded hover:bg-rose-100"
                  title="Delete"
                  data-testid={`delete-bonus-${p.amount}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bonus-editor-modal">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">{editing.id ? 'Edit package' : 'New package'}</h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Recharge amount (€)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={editing.amount}
                    onChange={(e) => setEditing({ ...editing, amount: parseFloat(e.target.value || 0) })}
                    data-testid="bonus-input-amount"
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Bonus (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={editing.bonus}
                    onChange={(e) => setEditing({ ...editing, bonus: parseFloat(e.target.value || 0) })}
                    data-testid="bonus-input-bonus"
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Label (optional)</label>
                <input
                  value={editing.label || ''}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="Starter / Pro / Premium…"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Color</label>
                  <select
                    value={editing.color}
                    onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg bg-white"
                  >
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Order</label>
                  <input
                    type="number"
                    min="1"
                    value={editing.order || 99}
                    onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 99, 10) })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Status</label>
                  <select
                    value={editing.enabled ? '1' : '0'}
                    onChange={(e) => setEditing({ ...editing, enabled: e.target.value === '1' })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg bg-white"
                  >
                    <option value="1">Enabled</option>
                    <option value="0">Hidden</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.popular}
                  onChange={(e) => setEditing({ ...editing, popular: e.target.checked })}
                  className="w-4 h-4 accent-amber-500"
                />
                <Star className={`w-4 h-4 ${editing.popular ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
                Mark as <strong>Popular</strong> (only one package can be popular at a time)
              </label>
            </div>
            <div className="flex gap-2 p-4 border-t border-slate-200">
              <button
                onClick={save}
                disabled={saving}
                data-testid="save-bonus-package"
                className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
